import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDateTime, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHeader, Card } from "@/components/Ui";
import { AntivirusStatus } from "@/components/AntivirusStatus";

const ICON = { conforme: [CheckCircle2, "text-emerald-600"], attenzione: [AlertTriangle, "text-amber-600"], non_conforme: [XCircle, "text-red-600"] };
const LABEL = { conforme: "Conforme", attenzione: "Attenzione", non_conforme: "Azione richiesta" };

export default function Compliance() {
  const [d, setD] = useState(null);
  const [sec, setSec] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/compliance").then((r) => setD(r.data)).catch(() => {});
    api.get("/security-checks").then((r) => setSec(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const runCheck = async () => {
    setBusy(true);
    try { const { data } = await api.post("/security-checks/run"); toast[data.failed ? "warning" : "success"](data.failed ? `${data.failed} anomalie rilevate` : "Controllo superato"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return <p className="text-slate-400">Caricamento…</p>;
  const last = sec[0];
  return (
    <div>
      <PageHeader eyebrow="ISO/IEC 27001:2022 · Dichiarazione di Applicabilità TM-ISMS-04" title="Stato di conformità" subtitle={`Verifica automatica dei controlli tecnici implementati nel CRM. Generato il ${fmtDateTime(d.generated_at)}.`} />
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <Card className="p-6 h-fit">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Controlli conformi</p>
          <p className="font-heading text-6xl font-bold text-slate-900 mt-3" data-testid="compliance-score">{d.score}%</p>
          <div className="h-2 bg-slate-100 rounded-full mt-4 overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${d.score}%` }} /></div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">I controlli organizzativi (formazione, fornitori, change management, audit interno) sono gestiti dalla manualistica SGSI e non sono misurabili dal software.</p>
          <div className="border-t border-slate-100 mt-6 pt-5" data-testid="security-check-card">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Controllo settimanale perimetro</p>
            <p className="text-[11px] text-slate-400 mt-1">Ogni lunedì alle 07:00, con email di allerta all'Amministratore</p>
            {last ? (
              <div className="mt-3 space-y-1" data-testid="security-check-last">
                <p className="text-xs text-slate-600">{fmtDateTime(last.created_at)} · <b className={last.failed ? "text-red-600" : "text-emerald-700"}>{last.failed ? `${last.failed} anomalie` : "superato"}</b></p>
                {last.checks.map((c, i) => (
                  <p key={i} className="text-[11px] flex gap-1.5"><span className={c.ok ? "text-emerald-600" : "text-red-600"}>{c.ok ? "✓" : "✗"}</span><span className="text-slate-600">{c.check}<span className="text-slate-400"> · {c.detail}</span></span></p>
                ))}
              </div>
            ) : <p className="text-xs text-slate-400 mt-3">Mai eseguito</p>}
            <Button size="sm" variant="outline" className="mt-4 w-full" disabled={busy} onClick={runCheck} data-testid="security-check-run">{busy ? "Controllo in corso…" : "Esegui ora"}</Button>
          </div>
          <AntivirusStatus />
        </Card>
        <Card className="divide-y divide-slate-100" data-testid="compliance-controls">
          {d.controls.map((c, i) => {
            const [I, cls] = ICON[c.status];
            return (
              <div key={i} className="p-4 flex items-start gap-4" data-testid={`compliance-control-${i}`}>
                <I className={`h-5 w-5 mt-0.5 shrink-0 ${cls}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{c.control}</span>
                    <p className="text-sm font-medium text-slate-900">{c.title}</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{c.detail}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-xs font-semibold ${cls}`}>{LABEL[c.status]}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{c.doc}</p>
                </div>
              </div>
            );
          })}
        </Card>
      </div>
    </div>
  );
}
